import type { TFunction } from "i18next";
import { NS, u3125FormatCurrency } from "../../../../U3125";
import {
  u3125ResolveSourceUsageStatus,
  u3125SourceUsageConsumedAmount,
  u3125SourceUsageOriginalAbs,
  u3125SourceUsageProgressPercent,
  type U3125InitCreateDocumentNature,
  type U3125SourceUsageSummary,
  type U3125SourceUsageStatus,
} from "../../../../U3125Service";

interface U3125SourceUsageSummaryBannerProps {
  summary: U3125SourceUsageSummary;
  documentNature: U3125InitCreateDocumentNature;
  t: TFunction;
  compact?: boolean;
}

function isCreditDocumentNature(documentNature: U3125InitCreateDocumentNature): boolean {
  return documentNature === "CREDIT";
}

function U3125TwoLineMetricLabel({ label }: { label: string }) {
  const match = label.match(/^(.+?)\s+(\([^)]+\))\s*$/);
  if (!match) return <>{label}</>;
  return (
    <>
      <span className="block leading-tight">{match[1].trim()}</span>
      <span className="block text-[10px] leading-tight">{match[2].trim()}</span>
    </>
  );
}

function statusLabel(status: U3125SourceUsageStatus, t: TFunction): string {
  if (status === "FULL") return t("lblSourceUsageStatusFull", { ns: NS });
  if (status === "PARTIAL") return t("lblSourceUsageStatusPartial", { ns: NS });
  return t("lblSourceUsageStatusUnused", { ns: NS });
}

function statusClassName(status: U3125SourceUsageStatus): string {
  if (status === "FULL") return "bg-slate-200 text-slate-800";
  if (status === "PARTIAL") return "bg-amber-100 text-amber-900";
  return "bg-emerald-100 text-emerald-900";
}

function progressBarClassName(status: U3125SourceUsageStatus): string {
  if (status === "FULL") return "bg-slate-500";
  if (status === "PARTIAL") return "bg-amber-500";
  return "bg-emerald-500";
}

function U3125SourceUsageSummaryBanner({ summary, documentNature, t, compact = false }: U3125SourceUsageSummaryBannerProps) {
  const status = u3125ResolveSourceUsageStatus(summary);
  const consumed = u3125SourceUsageConsumedAmount(summary);
  const original = u3125SourceUsageOriginalAbs(summary);
  const remaining = Math.max(0, Number(summary.remainingAmount) || 0);
  const progress = u3125SourceUsageProgressPercent(summary);
  const creditHt = isCreditDocumentNature(documentNature);
  const lblOriginal = creditHt ? "lblSourceUsageOriginalAmountBeforeTax" : "lblSourceUsageOriginalAmount";
  const lblApplied = creditHt ? "lblSourceUsageAppliedAmountBeforeTax" : "lblSourceUsageAppliedAmount";
  const lblRemaining = creditHt ? "lblSourceUsageRemainingAmountBeforeTax" : "lblSourceUsageRemainingAmount";

  return (
    <div className={`rounded-lg border border-gray-200 bg-muted/40 ${compact ? "p-3" : "p-4"} space-y-3`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{t("sectionSourceUsage", { ns: NS })}</span>
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClassName(status)}`}>
          {statusLabel(status, t)}
        </span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div className={`h-full rounded-full transition-all ${progressBarClassName(status)}`} style={{ width: `${progress}%` }} />
      </div>

      <p className="text-sm text-muted-foreground">
        {t("lblSourceUsageConsumed", {
          ns: NS,
          consumed: u3125FormatCurrency(consumed),
          original: u3125FormatCurrency(original),
        })}
        {" — "}
        {t("lblSourceUsageRemaining", { ns: NS, remaining: u3125FormatCurrency(remaining) })}
      </p>

      {!compact ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-md border border-gray-200 bg-surface px-3 py-2 text-right">
            <div className="text-xs text-muted-foreground">
              {creditHt ? <U3125TwoLineMetricLabel label={t(lblOriginal, { ns: NS })} /> : t(lblOriginal, { ns: NS })}
            </div>
            <div className="text-sm font-medium">{u3125FormatCurrency(original)}</div>
          </div>
          <div className="rounded-md border border-gray-200 bg-surface px-3 py-2 text-right">
            <div className="text-xs text-muted-foreground">
              {creditHt ? <U3125TwoLineMetricLabel label={t(lblApplied, { ns: NS })} /> : t(lblApplied, { ns: NS })}
            </div>
            <div className="text-sm font-medium">{u3125FormatCurrency(consumed)}</div>
          </div>
          <div className="rounded-md border border-gray-200 bg-surface px-3 py-2 text-right">
            <div className="text-xs text-muted-foreground">
              {creditHt ? <U3125TwoLineMetricLabel label={t(lblRemaining, { ns: NS })} /> : t(lblRemaining, { ns: NS })}
            </div>
            <div className="text-sm font-semibold">{u3125FormatCurrency(remaining)}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default U3125SourceUsageSummaryBanner;
